/**
 * Admin UI para destinos Telegram (ADR_007).
 *
 * Estructura:
 *   - Header card: estado del bot (getMe + 3 stats)
 *   - Tabla de destinos con badge de estado (Activo / Ensayo / Apagado)
 *   - Acciones por fila: Probar, Editar, Apagar/Encender, Borrar
 *   - Modal para crear nuevo destino
 *   - Modal para editar destino existente
 *
 * El "qué hace cada destino" se hardcodea en EVENT_DESCRIPTIONS — el
 * backend no expone esa relación (lo dispara cada poller con su nombre
 * de destino fijo). Si en el futuro se vuelve dinámico, agregamos un
 * campo `expected_event_types` al schema y leemos de ahí.
 */
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  Power,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import type {
  CrearReglaRuteoRequest,
  CrearUsuarioMenuRequest,
  CreateTelegramDestinationRequest,
  ReglaRuteo,
  TelegramBotHealth,
  TelegramDestination,
  TelegramResultado,
  TelegramShadowLogEntry,
  TelegramShadowLogFilters,
  TelegramShadowLogStats,
  TelegramStats,
} from "@/api/types";
import {
  useActualizarUsuarioMenu,
  useBorrarUsuarioMenu,
  useCrearUsuarioMenu,
  useUsuariosMenu,
  useActualizarRegla,
  useBorrarRegla,
  useCrearRegla,
  useReglasRuteo,
  useSimulacionRuteo,
  useCreateTelegramDestination,
  useDeleteTelegramDestination,
  useTelegramBotHealth,
  useTelegramDestinations,
  useTelegramShadowLog,
  useTelegramShadowLogStats,
  useTelegramStats,
  useTestTelegramDestination,
  useUpdateTelegramDestination,
} from "@/hooks/useTelegram";
import { cn } from "@/lib/utils";

// ────────────────────────────────────────────────────────────────────────────
// Qué recibe cada destino
//
// Sale de lo MEDIDO, no de una lista escrita a mano. La que había tenía tres
// entradas y dos nombraban destinos que no existen (`zabbix_problems`,
// `holanda_carritos`), así que la pantalla anunciaba "Sin disparador
// automático, solo prueba manual" para tres destinos que el 8-sep-2026
// recibían más de 1600 avisos al día. Una lista que hay que acordarse de
// actualizar se desincroniza; lo medido no puede.
//
// El backend manda `patrones_ruteo` (las reglas activas que apuntan aquí) y
// `trafico_7d` (lo que llegó de verdad). Este mapa sólo traduce el nombre
// técnico del evento a algo legible, y si no lo conoce muestra el nombre
// crudo, que sigue siendo cierto.

const NOMBRE_DE_EVENTO: Record<string, string> = {
  zabbix_problem: "alarmas de Zabbix",
  zabbix_resolution: "recuperaciones de Zabbix",
  cosecha_muda: "equipos que dejaron de cosechar",
  config_drift: "deriva de configuración",
  carrito_alarma_urgente: "alarma de carrito",
  carrito_resuelto: "carrito resuelto",
  carrito_notif_guardia: "aviso al guardia",
  agencia_reboot_auto: "reinicio automático de agencia",
  agencia_reboot_rechazado: "reinicio rechazado",
  agencia_reboot_recuperada: "agencia recuperada",
  agencia_reboot_no_accionable: "reinicio no accionable",
  agencia_reboot_sin_respuesta: "reinicio sin respuesta",
  test_smoke: "pruebas manuales",
};

function describeDestination(dest: TelegramDestination): {
  title: string;
  detail: string;
} {
  const trafico = (dest.trafico_7d ?? []).filter(
    (t) => t.event_type !== "test_smoke",
  );
  const patrones = dest.patrones_ruteo ?? [];
  const total = trafico.reduce((suma, t) => suma + t.total, 0);

  if (trafico.length === 0) {
    return {
      title: "Sin tráfico en 7 días",
      detail:
        patrones.length > 0
          ? `ruteado por: ${patrones.join(", ")}`
          : "ninguna regla apunta aquí",
    };
  }

  const nombres = trafico
    .slice(0, 3)
    .map((t) => NOMBRE_DE_EVENTO[t.event_type] ?? t.event_type);
  if (trafico.length > 3) nombres.push(`y ${trafico.length - 3} más`);

  const partes = [`${total.toLocaleString("es-MX")} avisos en 7 días`];
  if (patrones.length > 0) partes.push(`grupos: ${patrones.join(", ")}`);

  return { title: nombres.join(", "), detail: partes.join(" · ") };
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers de tiempo

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "nunca";
  const date = new Date(iso);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "hace segundos";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)} h`;
  return `hace ${Math.floor(seconds / 86_400)} d`;
}

function statusOf(d: TelegramDestination): "active" | "shadow" | "disabled" {
  if (!d.enabled) return "disabled";
  if (d.shadow_mode) return "shadow";
  return "active";
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principal

type TabKey = "destinations" | "routing" | "menu" | "shadow-log";

function parseTab(raw: string | null): TabKey {
  if (raw === "shadow-log") return "shadow-log";
  if (raw === "routing") return "routing";
  if (raw === "menu") return "menu";
  return "destinations";
}

export function AdminTelegramPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get("tab"));
  const botHealth = useTelegramBotHealth();
  const stats = useTelegramStats();

  const setTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "destinations") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Telegram</h1>
        <p className="text-sm text-muted-foreground">
          Un solo bot envía a todos los destinos. Activa, desactiva o pon en
          modo ensayo cada destino sin tocar el bot.
        </p>
      </header>

      <BotHealthCard health={botHealth.data ?? null} stats={stats.data ?? null} />

      <TabNav activeTab={activeTab} onChange={setTab} />

      {activeTab === "destinations" && <DestinationsTabPanel />}
      {activeTab === "routing" && <RoutingTabPanel />}
      {activeTab === "menu" && <MenuUsersTabPanel />}
      {activeTab === "shadow-log" && <ShadowLogTabPanel />}
    </div>
  );
}

function TabNav({
  activeTab,
  onChange,
}: {
  activeTab: TabKey;
  onChange: (tab: TabKey) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Vistas de Telegram"
      className="flex gap-1 border-b"
    >
      <TabButton
        active={activeTab === "destinations"}
        onClick={() => onChange("destinations")}
      >
        Destinos
      </TabButton>
      <TabButton
        active={activeTab === "routing"}
        onClick={() => onChange("routing")}
      >
        Reglas
      </TabButton>
      <TabButton active={activeTab === "menu"} onClick={() => onChange("menu")}>
        Menú
      </TabButton>
      <TabButton
        active={activeTab === "shadow-log"}
        onClick={() => onChange("shadow-log")}
      >
        Historial de notificaciones
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function DestinationsTabPanel() {
  const destinations = useTelegramDestinations();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TelegramDestination | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TelegramDestination | null>(null);

  const items = destinations.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Destinos</h2>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="size-4" />
          Nuevo destino
        </button>
      </div>

      {destinations.isPending && !destinations.data ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          No hay destinos configurados. Crea el primero con el botón de arriba.
        </div>
      ) : (
        <DestinationsTable
          items={items}
          onEdit={(d) => setEditing(d)}
          onDelete={(d) => setConfirmDelete(d)}
        />
      )}

      {createOpen && (
        <CreateDestinationModal onClose={() => setCreateOpen(false)} />
      )}

      {editing && (
        <EditDestinationModal
          destination={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          destination={confirmDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab Historial de notificaciones (ADR_008)
//
// Muestra los cuatro desenlaces del dispatcher, no solo el de ensayo. Antes de
// la migracion 062 un destino que enviaba de verdad no dejaba fila, asi que las
// notificaciones que si salieron no tenian historial en ninguna parte.

const RESULTADOS: {
  value: TelegramResultado;
  label: string;
  className: string;
}[] = [
  {
    value: "sent",
    label: "Enviado",
    className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  },
  {
    value: "shadow",
    label: "Ensayo",
    className: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  },
  {
    value: "disabled",
    label: "Apagado",
    className: "bg-muted text-muted-foreground",
  },
  {
    value: "send_failed",
    label: "Falló",
    className: "bg-red-500/15 text-red-700 dark:text-red-400",
  },
];

const TIME_WINDOWS: { value: string; label: string; hours: number | null }[] = [
  { value: "24h", label: "Últimas 24 h", hours: 24 },
  { value: "7d", label: "Últimos 7 días", hours: 24 * 7 },
  { value: "30d", label: "Últimos 30 días", hours: 24 * 30 },
  { value: "all", label: "Sin filtro de tiempo", hours: null },
];

function ShadowLogTabPanel() {
  const [destinationFilter, setDestinationFilter] = useState<string>("");
  const [eventFilter, setEventFilter] = useState<string>("");
  const [resultadoFilter, setResultadoFilter] = useState<string>("");
  const [windowKey, setWindowKey] = useState<string>("7d");
  const [page, setPage] = useState<number>(1);
  const perPage = 20;

  const sinceIso = useMemo(() => {
    const w = TIME_WINDOWS.find((x) => x.value === windowKey);
    if (!w?.hours) return undefined;
    const since = new Date(Date.now() - w.hours * 60 * 60 * 1000);
    // `Z` en lugar de `+00:00` para que URLSearchParams no lo decode raro.
    return since.toISOString();
  }, [windowKey]);

  // Resetea pagina cuando cambia un filtro (los filtros recortan total).
  const filters: TelegramShadowLogFilters = useMemo(
    () => ({
      destination_name: destinationFilter || undefined,
      event_type: eventFilter || undefined,
      resultado: (resultadoFilter || undefined) as TelegramResultado | undefined,
      since: sinceIso,
      page,
      per_page: perPage,
    }),
    [destinationFilter, eventFilter, resultadoFilter, sinceIso, page],
  );

  const { data, isPending, error } = useTelegramShadowLog(filters);
  const stats = useTelegramShadowLogStats();
  const destinations = useTelegramDestinations();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Cuando cambian los filtros, vuelve a página 1.
  const onChangeFilter = (cb: () => void) => {
    cb();
    setPage(1);
  };

  // Lista de event_types únicos vistos en los items actuales (para el select).
  // No es 100% exhaustivo si hay muchas páginas, pero es suficiente UX.
  const distinctEventTypes = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) set.add(it.event_type);
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="space-y-4">
      <ShadowLogStatsCards stats={stats.data ?? null} />

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={destinationFilter}
          onChange={(e) =>
            onChangeFilter(() => setDestinationFilter(e.target.value))
          }
          aria-label="Filtrar por destino"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los destinos</option>
          {(destinations.data?.items ?? []).map((d) => (
            <option key={d.id} value={d.name}>
              {d.label}
            </option>
          ))}
        </select>

        <select
          value={eventFilter}
          onChange={(e) => onChangeFilter(() => setEventFilter(e.target.value))}
          aria-label="Filtrar por tipo de evento"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los eventos</option>
          {distinctEventTypes.map((et) => (
            <option key={et} value={et}>
              {et}
            </option>
          ))}
        </select>

        <select
          value={resultadoFilter}
          onChange={(e) =>
            onChangeFilter(() => setResultadoFilter(e.target.value))
          }
          aria-label="Filtrar por desenlace"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los desenlaces</option>
          {RESULTADOS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <select
          value={windowKey}
          onChange={(e) => onChangeFilter(() => setWindowKey(e.target.value))}
          aria-label="Ventana de tiempo"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {TIME_WINDOWS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error instanceof ApiError
            ? error.message
            : "Error al cargar el historial."}
        </div>
      )}

      {isPending && !data ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          No hay eventos en esta ventana con los filtros activos.
        </div>
      ) : (
        <ShadowLogTable items={items} />
      )}

      {items.length > 0 && (
        <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-3 rounded-md">
          <div className="text-xs text-muted-foreground">
            Mostrando {items.length} de {total} entradas · página {page} de{" "}
            {totalPages}
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-md border px-3 py-1 text-xs font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              ← Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border px-3 py-1 text-xs font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShadowLogStatsCards({
  stats,
}: {
  stats: TelegramShadowLogStats | null;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard label="Últimas 24 h" value={stats?.last_24h ?? 0} />
      <StatCard label="Últimos 7 d" value={stats?.last_7d ?? 0} />
      <StatCard
        label="Destinos activos hoy"
        value={stats?.distinct_destinations_today ?? 0}
      />
      <StatCard
        label="Tipos de evento (24h)"
        value={stats?.distinct_event_types_24h ?? 0}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-muted/40 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

function ResultadoBadge({ resultado }: { resultado: TelegramResultado }) {
  // Un desenlace desconocido se muestra tal cual en vez de caer a "Ensayo":
  // pintar de ensayo algo que si salio seria peor que no reconocerlo.
  const def = RESULTADOS.find((r) => r.value === resultado);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        def?.className ?? "bg-muted text-muted-foreground",
      )}
    >
      {def?.label ?? resultado}
    </span>
  );
}

function ShadowLogTable({ items }: { items: TelegramShadowLogEntry[] }) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="grid grid-cols-12 gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
        <div className="col-span-2">Cuándo</div>
        <div className="col-span-3">Destino · Evento</div>
        <div className="col-span-5">Preview</div>
        <div className="col-span-1">Desenlace</div>
        <div className="col-span-1 text-right">Detalle</div>
      </div>
      {items.map((entry) => (
        <ShadowLogRow key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function ShadowLogRow({ entry }: { entry: TelegramShadowLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  // Preview: primeras 120 chars del rendered_text, sin tags HTML.
  const preview = useMemo(() => {
    const stripped = entry.rendered_text.replace(/<[^>]+>/g, " ");
    return stripped.length > 120 ? stripped.slice(0, 120) + "…" : stripped;
  }, [entry.rendered_text]);

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-12 items-center gap-3 border-b px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30",
          expanded && "bg-muted/30",
        )}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="col-span-2 text-xs text-muted-foreground">
          {formatRelative(entry.would_send_at)}
        </div>
        <div className="col-span-3 min-w-0">
          <div className="truncate text-sm font-medium">
            {entry.destination_name}
          </div>
          <div className="truncate font-mono text-xs text-muted-foreground">
            {entry.event_type}
          </div>
        </div>
        <div className="col-span-5 truncate text-xs text-muted-foreground">
          {preview}
        </div>
        <div className="col-span-1">
          <ResultadoBadge resultado={entry.resultado} />
        </div>
        <div className="col-span-1 text-right text-muted-foreground">
          {expanded ? (
            <ChevronUp className="ml-auto size-4" aria-label="Colapsar" />
          ) : (
            <ChevronDown className="ml-auto size-4" aria-label="Expandir" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="grid grid-cols-12 gap-3 border-b bg-muted/15 px-4 py-3">
          <div className="col-span-7">
            <div className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
              Mensaje renderizado
            </div>
            <div
              className="rounded-md border bg-background p-3 text-sm leading-relaxed"
              // El rendered_text es HTML (Jinja2 templates con tags <b>, <code>).
              // Es contenido controlado del backend, no input de usuario, así
              // que dangerouslySetInnerHTML es seguro aquí.
              dangerouslySetInnerHTML={{ __html: entry.rendered_text }}
            />
          </div>
          <div className="col-span-5">
            <div className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
              Decisión y razón
            </div>
            <table className="w-full text-xs">
              <tbody>
                <tr>
                  <td className="py-0.5 text-muted-foreground">Estado:</td>
                  <td className="py-0.5 text-right">Ensayo (no enviado)</td>
                </tr>
                <tr>
                  <td className="py-0.5 text-muted-foreground">Razón:</td>
                  <td className="py-0.5 text-right font-mono">
                    {entry.decision_reason}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 text-muted-foreground">Destino:</td>
                  <td className="py-0.5 text-right">
                    {entry.destination_name}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 text-muted-foreground">Evento:</td>
                  <td className="py-0.5 text-right font-mono">
                    {entry.event_type}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 text-muted-foreground">Cuándo:</td>
                  <td className="py-0.5 text-right">
                    {new Date(entry.would_send_at).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Para que este tipo de evento empiece a enviarse a Telegram, edita
              el destino y desactiva el modo ensayo.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Header card del bot

function BotHealthCard({
  health,
  stats,
}: {
  health: TelegramBotHealth | null;
  stats: TelegramStats | null;
}) {
  const connected = health?.connected ?? false;
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-11 items-center justify-center rounded-full",
            connected
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
              : "bg-muted text-muted-foreground",
          )}
        >
          <Send className="size-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Bot AROC</span>
            {connected ? (
              <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                Conectado
              </span>
            ) : (
              <span className="rounded-md bg-destructive/15 px-2 py-0.5 text-[11px] font-medium text-destructive">
                {health?.error ?? "Desconectado"}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {connected
              ? `@${health?.username ?? "—"} · último evento ${formatRelative(health?.last_event_at)}`
              : "Revisa la variable TELEGRAM_BOT_TOKEN en el .env del despliegue."}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 border-t pt-4">
        <StatTile label="Destinos activos" value={stats?.active_count ?? 0} />
        <StatTile label="En modo ensayo" value={stats?.shadow_count ?? 0} />
        <StatTile
          label="Eventos hoy"
          value={stats?.events_today ?? 0}
          hint="solo cuenta destinos en ensayo"
        />
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-md bg-muted/40 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tabla de destinos

function DestinationsTable({
  items,
  onEdit,
  onDelete,
}: {
  items: TelegramDestination[];
  onEdit: (d: TelegramDestination) => void;
  onDelete: (d: TelegramDestination) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="grid grid-cols-12 gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
        <div className="col-span-3">Destino</div>
        <div className="col-span-3">Recibe</div>
        <div className="col-span-2">Estado</div>
        <div className="col-span-2">Último evento</div>
        <div className="col-span-2 text-right">Acciones</div>
      </div>
      {items.map((d) => (
        <DestinationRow
          key={d.id}
          dest={d}
          onEdit={() => onEdit(d)}
          onDelete={() => onDelete(d)}
        />
      ))}
    </div>
  );
}

function DestinationRow({
  dest,
  onEdit,
  onDelete,
}: {
  dest: TelegramDestination;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const testMut = useTestTelegramDestination();
  const updateMut = useUpdateTelegramDestination();
  const [feedback, setFeedback] = useState<string | null>(null);

  const status = statusOf(dest);
  const desc = describeDestination(dest);

  const handleTest = async () => {
    setFeedback(null);
    try {
      const res = await testMut.mutateAsync({
        destination_name: dest.name,
      });
      setFeedback(
        res.status === "sent"
          ? "Mensaje enviado."
          : res.status === "shadow"
            ? "Mensaje registrado en ensayo (no se envió a Telegram)."
            : "Destino apagado — sin envío.",
      );
    } catch (err) {
      setFeedback(err instanceof ApiError ? err.message : "Error al probar.");
    }
  };

  const handleToggle = async () => {
    try {
      await updateMut.mutateAsync({
        name: dest.name,
        body: { enabled: !dest.enabled },
      });
    } catch (err) {
      setFeedback(
        err instanceof ApiError ? err.message : "Error al cambiar estado.",
      );
    }
  };

  return (
    <div className="grid grid-cols-12 items-center gap-3 border-b px-4 py-3 last:border-b-0">
      <div className="col-span-3 min-w-0">
        <div className="text-sm font-medium">{dest.label}</div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {dest.chat_id}
        </div>
      </div>
      <div className="col-span-3">
        <div className="text-sm">{desc.title}</div>
        <div className="text-xs text-muted-foreground">{desc.detail}</div>
      </div>
      <div className="col-span-2">
        <StatusBadge status={status} />
      </div>
      <div className="col-span-2 text-xs text-muted-foreground">
        {formatRelative(dest.updated_at)}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={handleTest}
          disabled={testMut.isPending}
          className="rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          {testMut.isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            "Probar"
          )}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={handleToggle}
          disabled={updateMut.isPending}
          title={dest.enabled ? "Apagar" : "Encender"}
          aria-label={dest.enabled ? "Apagar destino" : "Encender destino"}
          className="rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Power className="size-3" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Borrar destino"
          className="rounded-md border border-destructive/30 px-2 py-1 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          <Trash2 className="size-3" />
        </button>
      </div>
      {feedback && (
        <div className="col-span-12 mt-1 text-xs text-muted-foreground">
          {feedback}
        </div>
      )}
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: "active" | "shadow" | "disabled";
}) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
        ● Activo
      </span>
    );
  }
  if (status === "shadow") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        ● Ensayo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      ● Apagado
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modales

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // ESC para cerrar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border bg-card p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-md p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateDestinationModal({ onClose }: { onClose: () => void }) {
  const createMut = useCreateTelegramDestination();
  const [form, setForm] = useState<CreateTelegramDestinationRequest>({
    name: "",
    label: "",
    chat_id: "",
    enabled: false,
    shadow_mode: true,
    description: "",
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!/^[a-z0-9_]+$/.test(form.name)) {
      setError(
        "El nombre solo acepta minúsculas, números y guion bajo (ej: noc_tilatina).",
      );
      return;
    }
    if (form.label.trim().length === 0) {
      setError("La etiqueta no puede estar vacía.");
      return;
    }
    if (form.chat_id.trim().length === 0) {
      setError("El chat ID no puede estar vacío.");
      return;
    }
    try {
      await createMut.mutateAsync(form);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error al crear.");
    }
  };

  return (
    <ModalShell title="Nuevo destino Telegram" onClose={onClose}>
      <DestinationForm form={form} setForm={setForm} />
      {error && (
        <div role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={createMut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createMut.isPending && <Loader2 className="size-4 animate-spin" />}
          Crear destino
        </button>
      </div>
    </ModalShell>
  );
}

function EditDestinationModal({
  destination,
  onClose,
}: {
  destination: TelegramDestination;
  onClose: () => void;
}) {
  const updateMut = useUpdateTelegramDestination();
  // El `name` no se edita — es la clave estable. Se muestra read-only.
  const [form, setForm] = useState<CreateTelegramDestinationRequest>({
    name: destination.name,
    label: destination.label,
    chat_id: destination.chat_id,
    enabled: destination.enabled,
    shadow_mode: destination.shadow_mode,
    description: destination.description,
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await updateMut.mutateAsync({
        name: destination.name,
        body: {
          label: form.label,
          chat_id: form.chat_id,
          enabled: form.enabled,
          shadow_mode: form.shadow_mode,
          description: form.description,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error al guardar.");
    }
  };

  return (
    <ModalShell title={`Editar ${destination.label}`} onClose={onClose}>
      <DestinationForm form={form} setForm={setForm} disableName />
      {error && (
        <div role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={updateMut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateMut.isPending && <Loader2 className="size-4 animate-spin" />}
          Guardar
        </button>
      </div>
    </ModalShell>
  );
}

function DestinationForm({
  form,
  setForm,
  disableName = false,
}: {
  form: CreateTelegramDestinationRequest;
  setForm: (next: CreateTelegramDestinationRequest) => void;
  disableName?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="tg-name" className="mb-1 block text-xs font-medium">
          Nombre interno (slug)
        </label>
        <input
          id="tg-name"
          type="text"
          value={form.name}
          disabled={disableName}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="noc_tilatina"
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
          maxLength={64}
        />
        {disableName && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            El nombre es la clave estable. Para cambiarlo, crea uno nuevo y borra
            el actual.
          </p>
        )}
      </div>
      <div>
        <label htmlFor="tg-label" className="mb-1 block text-xs font-medium">
          Etiqueta visible
        </label>
        <input
          id="tg-label"
          type="text"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="NOC Tilatina"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          maxLength={128}
        />
      </div>
      <div>
        <label htmlFor="tg-chat" className="mb-1 block text-xs font-medium">
          Chat ID de Telegram
        </label>
        <input
          id="tg-chat"
          type="text"
          value={form.chat_id}
          onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
          placeholder="-1001234567890"
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          maxLength={64}
        />
      </div>
      <div>
        <label
          htmlFor="tg-desc"
          className="mb-1 block text-xs font-medium"
        >
          Descripción (opcional)
        </label>
        <textarea
          id="tg-desc"
          value={form.description ?? ""}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          maxLength={1024}
        />
      </div>
      <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.enabled ?? false}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          Activar destino (`enabled`)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.shadow_mode ?? true}
            onChange={(e) =>
              setForm({ ...form, shadow_mode: e.target.checked })
            }
          />
          Modo ensayo (`shadow_mode`) — registra en log sin enviar
        </label>
        <p className="text-[11px] text-muted-foreground">
          Recomendado: crear primero en ensayo, validar con "Probar", y solo
          después desactivar el ensayo para que envíe de verdad.
        </p>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  destination,
  onClose,
}: {
  destination: TelegramDestination;
  onClose: () => void;
}) {
  const deleteMut = useDeleteTelegramDestination();
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await deleteMut.mutateAsync(destination.name);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error al borrar.");
    }
  };

  return (
    <ModalShell title="Borrar destino" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <AlertCircle className="mt-0.5 size-4 text-destructive" />
          <div>
            <div className="font-medium text-destructive">
              ¿Borrar {destination.label}?
            </div>
            <p className="text-xs text-muted-foreground">
              Esta acción es permanente. El nombre{" "}
              <code className="font-mono">{destination.name}</code> quedará
              libre para reutilizarse.
            </p>
          </div>
        </div>
        {error && (
          <div role="alert" className="text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={deleteMut.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteMut.isPending && <Loader2 className="size-4 animate-spin" />}
            Borrar definitivamente
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Reglas de ruteo: qué alarma va a qué grupo
//
// Antes esto vivía en el código, buscando la etiqueta `module`. Medido el
// 25-ago-2026: ningún disparador de esta casa la trae —el Zabbix propio tiene
// 219 vigilados, 87 sin ninguna etiqueta y el resto sólo `scope`— así que TODO
// caía en el destino por omisión y el grupo de Emporia nunca recibía sus
// carritos. El error era invisible porque los mensajes sí llegaban, a otro
// lado. De ahí el simulador: ver a dónde caería cada alarma ANTES de que caiga.

const CELDA = "px-3 py-2 align-top";
const BOTON_SEC =
  "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-accent";

function RoutingTabPanel() {
  const { data, isLoading, isError } = useReglasRuteo();
  // Las reglas guardan el nombre tecnico del destino (`nova_holanda`) y la
  // pestana de Destinos muestra su etiqueta (`Agencias Holanda`). Sin cruzar
  // las dos, las pantallas parecen hablar de cosas distintas y da la impresion
  // de que el ruteo no cuadra, cuando si cuadra. Paso medido el 8-sep-2026, al
  // renombrar las etiquetas de los cuatro destinos.
  const { data: destinosData } = useTelegramDestinations();
  const destinoDe = useMemo(() => {
    const m = new Map<string, TelegramDestination>();
    for (const d of destinosData?.items ?? []) m.set(d.name, d);
    return m;
  }, [destinosData]);
  const crear = useCrearRegla();
  const actualizar = useActualizarRegla();
  const borrar = useBorrarRegla();
  const [simulando, setSimulando] = useState(false);
  const [nueva, setNueva] = useState(false);
  const [porBorrar, setPorBorrar] = useState<ReglaRuteo | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const reglas = data?.items ?? [];
  const destinos = data?.destinos_validos ?? [];
  const enError = (e: unknown) =>
    setErrorAccion(e instanceof ApiError ? e.message : "No se pudo guardar la regla.");

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-lg font-medium">Reglas de ruteo</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Gana la <strong>primera</strong> que empata, de arriba hacia abajo: el orden
          es la regla. Lo que no empata y pasa el umbral de severidad va a{" "}
          <code className="font-mono">noc_general</code>. Aquí lo que funciona es
          rutear por <em>grupo de host</em>: las etiquetas de los disparadores están
          casi todas vacías.
        </p>

        {errorAccion && (
          <p className="mt-3 text-sm text-destructive">{errorAccion}</p>
        )}
        {isLoading && <p className="mt-3 text-sm text-muted-foreground">Cargando…</p>}
        {isError && (
          <p className="mt-3 text-sm text-destructive">
            No se pudieron leer las reglas.
          </p>
        )}

        {!isLoading && !isError && (
          <table className="mt-3 w-full text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className={`${CELDA} text-left`}>Orden</th>
                <th className={`${CELDA} text-left`}>Si empata con</th>
                <th className={`${CELDA} text-left`}>Va a</th>
                <th className={`${CELDA} text-left`}>Activa</th>
                <th className={CELDA} />
              </tr>
            </thead>
            <tbody>
              {reglas.map((r) => (
                <tr key={r.id} className={`border-b ${r.activa ? "" : "opacity-50"}`}>
                  <td className={`${CELDA} tabular-nums`}>{r.posicion}</td>
                  <td className={CELDA}>
                    <span className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground">
                      {r.tipo === "host_group" ? "grupo" : `etiqueta ${r.campo}`}
                    </span>{" "}
                    <code className="font-mono">{r.patron}</code>
                    {r.descripcion && (
                      <div className="text-xs text-muted-foreground">{r.descripcion}</div>
                    )}
                  </td>
                  <td className={CELDA}>
                    <code className="font-mono">{r.destino}</code>
                    {destinoDe.get(r.destino) && (
                      <div className="text-xs text-muted-foreground">
                        {destinoDe.get(r.destino)?.label}
                      </div>
                    )}
                    {!destinos.includes(r.destino) && (
                      <span className="ml-2 rounded bg-destructive px-1.5 py-0.5 text-xs text-destructive-foreground">
                        no existe
                      </span>
                    )}
                    <EstadoDelDestino destino={destinoDe.get(r.destino)} />
                  </td>
                  <td className={CELDA}>
                    <button
                      type="button"
                      className="text-sm underline-offset-2 hover:underline"
                      onClick={() =>
                        actualizar.mutate(
                          { id: r.id, cambios: { activa: !r.activa } },
                          { onError: enError },
                        )
                      }
                    >
                      {r.activa ? "Sí" : "No"}
                    </button>
                  </td>
                  <td className={CELDA}>
                    <button
                      type="button"
                      className="text-sm text-destructive underline-offset-2 hover:underline"
                      onClick={() => {
                        setErrorAccion(null);
                        setPorBorrar(r);
                      }}
                    >
                      Borrar
                    </button>
                  </td>
                </tr>
              ))}
              {reglas.length === 0 && (
                <tr>
                  <td colSpan={5} className={`${CELDA} text-center text-muted-foreground`}>
                    Sin reglas: todas las alarmas van al destino por omisión.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={BOTON_SEC} onClick={() => setNueva((v) => !v)}>
            {nueva ? "Cancelar" : "Nueva regla"}
          </button>
          <button
            type="button"
            className={BOTON_SEC}
            onClick={() => setSimulando((v) => !v)}
          >
            {simulando ? "Ocultar simulación" : "Probar con alarmas reales"}
          </button>
        </div>

        {nueva && (
          <NuevaReglaForm
            destinos={destinos}
            siguientePosicion={(reglas.at(-1)?.posicion ?? 0) + 10}
            onCancelar={() => setNueva(false)}
            onGuardar={(body) =>
              crear.mutate(body, {
                onSuccess: () => {
                  setNueva(false);
                  setErrorAccion(null);
                },
                onError: enError,
              })
            }
          />
        )}
      </div>

      {porBorrar && (
        <ConfirmarBorrarReglaModal
          regla={porBorrar}
          onClose={() => setPorBorrar(null)}
          onConfirmar={() =>
            borrar.mutate(porBorrar.id, {
              onSuccess: () => setPorBorrar(null),
              onError: (e) => {
                setPorBorrar(null);
                enError(e);
              },
            })
          }
          pendiente={borrar.isPending}
        />
      )}

      {simulando && <SimulacionPanel />}
    </div>
  );
}

// Una regla puede apuntar a un destino apagado o en ensayo, y entonces sus
// alarmas no llegan: el despachador las descarta o las escribe nada más en el
// historial. La regla se ve igual de sana en la tabla, y ese es justo el tipo
// de fallo que esta pantalla existe para atrapar antes y no semanas después.
function EstadoDelDestino({ destino }: { destino?: TelegramDestination }) {
  if (!destino) return null;
  if (!destino.enabled) {
    return (
      <div className="mt-1 text-xs text-destructive">
        El destino está apagado: estas alarmas no se envían.
      </div>
    );
  }
  if (destino.shadow_mode) {
    return (
      <div className="mt-1 text-xs text-amber-600 dark:text-amber-500">
        El destino está en ensayo: se registran, no se envían.
      </div>
    );
  }
  return null;
}

function ConfirmarBorrarReglaModal({
  regla,
  onClose,
  onConfirmar,
  pendiente,
}: {
  regla: ReglaRuteo;
  onClose: () => void;
  onConfirmar: () => void;
  pendiente: boolean;
}) {
  return (
    <ModalShell title="Borrar regla de ruteo" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <AlertCircle className="mt-0.5 size-4 text-destructive" />
          <div>
            <div className="font-medium text-destructive">
              ¿Borrar la regla{" "}
              <code className="font-mono">{regla.patron}</code>?
            </div>
            <p className="text-xs text-muted-foreground">
              {regla.activa ? (
                <>
                  A partir de ahora, lo que hoy va a{" "}
                  <code className="font-mono">{regla.destino}</code> por esta regla
                  caerá donde lo mande la primera regla que empate, o en{" "}
                  <code className="font-mono">noc_general</code> si no empata
                  ninguna. Se seguirá enviando, sólo que a otro grupo, así que
                  nada va a avisar del cambio.
                </>
              ) : (
                <>
                  Está apagada, así que hoy no rutea nada. Borrarla no cambia a
                  dónde llega ninguna alarma.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={pendiente}
            className="inline-flex items-center gap-2 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pendiente && <Loader2 className="size-4 animate-spin" />}
            Borrar definitivamente
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function NuevaReglaForm({
  destinos,
  siguientePosicion,
  onGuardar,
  onCancelar,
}: {
  destinos: string[];
  siguientePosicion: number;
  onGuardar: (body: CrearReglaRuteoRequest) => void;
  onCancelar: () => void;
}) {
  const [tipo, setTipo] = useState<"host_group" | "tag">("host_group");
  // Vacío a propósito. Antes venía con `module` puesto, que es la etiqueta que
  // el ruteo viejo buscaba y que está medido (25-ago-2026) que ningún
  // disparador de esta casa trae: una regla guardada con ese valor por omisión
  // no empata nunca y se ve igual de sana que las demás.
  const [campo, setCampo] = useState("");
  const [patron, setPatron] = useState("");
  const [destino, setDestino] = useState(destinos[0] ?? "");
  const [descripcion, setDescripcion] = useState("");
  const campoCls =
    "rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div className="mt-4 space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Empata por</span>
          <select
            className={campoCls}
            value={tipo}
            onChange={(e) => setTipo(e.target.value as "host_group" | "tag")}
          >
            <option value="host_group">Grupo de host</option>
            <option value="tag">Etiqueta</option>
          </select>
        </label>
        {tipo === "tag" && (
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground">Etiqueta</span>
            <input
              className={campoCls}
              value={campo}
              onChange={(e) => setCampo(e.target.value)}
            />
          </label>
        )}
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Que contenga</span>
          <input
            className={campoCls}
            value={patron}
            placeholder={tipo === "host_group" ? "Emporia" : "emporia"}
            onChange={(e) => setPatron(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Va al destino</span>
          <select
            className={campoCls}
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
          >
            {destinos.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="grow text-sm">
          <span className="block text-xs text-muted-foreground">Para qué es</span>
          <input
            className={`${campoCls} w-full`}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!patron.trim() || !destino || (tipo === "tag" && !campo.trim())}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          onClick={() =>
            onGuardar({
              posicion: siguientePosicion,
              tipo,
              campo: tipo === "tag" ? campo.trim() : "",
              patron: patron.trim(),
              destino,
              activa: true,
              descripcion,
            })
          }
        >
          Guardar
        </button>
        <button type="button" className={BOTON_SEC} onClick={onCancelar}>
          Cancelar
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Se agrega al final. Un patrón vacío no empataría con nada, por eso no se puede
        guardar así.{" "}
        {tipo === "tag" && (
          <>
            Y antes de rutear por etiqueta, compruébala en la simulación: los
            disparadores de esta casa casi no traen etiquetas, así que una regla
            así se ve sana y no empata nunca.
          </>
        )}
      </p>
    </div>
  );
}

function SimulacionPanel() {
  const { data, isLoading, isError, error } = useSimulacionRuteo(true);
  const filas = data?.items ?? [];

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="text-lg font-medium">A dónde caería cada alarma vigente</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        No manda nada: evalúa las reglas de ahora contra los problemas abiertos en
        Zabbix. Es la forma de ver un error de ruteo <em>antes</em> de que las alarmas
        lleven semanas llegando al grupo equivocado.
      </p>
      {isLoading && (
        <p className="mt-3 text-sm text-muted-foreground">Consultando Zabbix…</p>
      )}
      {isError && (
        <p className="mt-3 text-sm text-destructive">
          {error instanceof ApiError ? error.message : "No se pudo simular."}
        </p>
      )}
      {!isLoading && !isError && (
        <table className="mt-3 w-full text-sm">
          <thead className="border-b text-xs uppercase text-muted-foreground">
            <tr>
              <th className={`${CELDA} text-left`}>Equipo</th>
              <th className={`${CELDA} text-left`}>Grupo de Zabbix</th>
              <th className={`${CELDA} text-left`}>Problema</th>
              <th className={`${CELDA} text-left`}>Caería en</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={`${f.host}-${i}`} className="border-b">
                <td className={CELDA}>{f.host}</td>
                <td className={`${CELDA} text-muted-foreground`}>{f.grupo}</td>
                <td className={`${CELDA} max-w-md truncate`} title={f.trigger}>
                  {f.trigger}
                </td>
                <td className={CELDA}>
                  {f.destino ? (
                    <>
                      <code className="font-mono">{f.destino}</code>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {f.regla_id ? `regla #${f.regla_id}` : "por omisión"}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">no se manda</span>
                  )}
                </td>
              </tr>
            ))}
            {filas.length === 0 && (
              <tr>
                <td colSpan={4} className={`${CELDA} text-center text-muted-foreground`}>
                  No hay alarmas vigentes ahora mismo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Quién puede usar el menú del bot
//
// Antes vivía en `NOC_BOT_AUTHORIZED_TELEGRAM_IDS`: agregar a alguien exigía
// SSH al servidor, editar el archivo y recrear el contenedor — para un dato que
// cambia cada vez que entra o sale una persona del NOC. El poller lee la tabla
// **por mensaje**, así que un alta surte efecto de inmediato.

function MenuUsersTabPanel() {
  const { data, isLoading, isError } = useUsuariosMenu();
  const crear = useCrearUsuarioMenu();
  const actualizar = useActualizarUsuarioMenu();
  const borrar = useBorrarUsuarioMenu();
  const [nuevo, setNuevo] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const usuarios = data?.items ?? [];
  const enError = (e: unknown) =>
    setErrorAccion(e instanceof ApiError ? e.message : "No se pudo guardar.");

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-lg font-medium">Quién puede usar el menú</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Con <code className="font-mono">/menu</code> se consulta el estado de la
          red desde Telegram. Sólo la gente de esta lista obtiene respuesta; a
          cualquier otra el bot la ignora en silencio. Los cambios surten efecto de
          inmediato, sin reiniciar nada.
        </p>

        {errorAccion && <p className="mt-3 text-sm text-destructive">{errorAccion}</p>}
        {isLoading && <p className="mt-3 text-sm text-muted-foreground">Cargando…</p>}
        {isError && (
          <p className="mt-3 text-sm text-destructive">No se pudo leer la lista.</p>
        )}

        {!isLoading && !isError && (
          <table className="mt-3 w-full text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className={`${CELDA} text-left`}>Persona</th>
                <th className={`${CELDA} text-left`}>Id de Telegram</th>
                <th className={`${CELDA} text-left`}>Puede usarlo</th>
                <th className={CELDA} />
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className={`border-b ${u.activo ? "" : "opacity-50"}`}>
                  <td className={CELDA}>
                    {u.nombre || <span className="text-muted-foreground">sin nombre</span>}
                    {u.descripcion && (
                      <div className="text-xs text-muted-foreground">{u.descripcion}</div>
                    )}
                  </td>
                  <td className={`${CELDA} font-mono tabular-nums`}>{u.telegram_id}</td>
                  <td className={CELDA}>
                    <button
                      type="button"
                      className="text-sm underline-offset-2 hover:underline"
                      onClick={() =>
                        actualizar.mutate(
                          { id: u.id, cambios: { activo: !u.activo } },
                          { onError: enError },
                        )
                      }
                    >
                      {u.activo ? "Sí" : "No"}
                    </button>
                  </td>
                  <td className={CELDA}>
                    <button
                      type="button"
                      className="text-sm text-destructive underline-offset-2 hover:underline"
                      onClick={() => borrar.mutate(u.id, { onError: enError })}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
              {usuarios.length === 0 && (
                <tr>
                  <td colSpan={4} className={`${CELDA} text-center text-muted-foreground`}>
                    Lista vacía: el bot no le responde el menú a nadie.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="mt-4">
          <button type="button" className={BOTON_SEC} onClick={() => setNuevo((v) => !v)}>
            {nuevo ? "Cancelar" : "Autorizar a alguien"}
          </button>
        </div>

        {nuevo && (
          <NuevoUsuarioMenuForm
            onCancelar={() => setNuevo(false)}
            onGuardar={(body) =>
              crear.mutate(body, {
                onSuccess: () => {
                  setNuevo(false);
                  setErrorAccion(null);
                },
                onError: enError,
              })
            }
          />
        )}
      </div>
    </div>
  );
}

function NuevoUsuarioMenuForm({
  onGuardar,
  onCancelar,
}: {
  onGuardar: (body: CrearUsuarioMenuRequest) => void;
  onCancelar: () => void;
}) {
  const [telegramId, setTelegramId] = useState("");
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const campoCls =
    "rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
  const idValido = /^\d+$/.test(telegramId.trim());

  return (
    <div className="mt-4 space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Id de Telegram</span>
          <input
            className={campoCls}
            value={telegramId}
            placeholder="584516519"
            inputMode="numeric"
            onChange={(e) => setTelegramId(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Nombre</span>
          <input
            className={campoCls}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>
        <label className="grow text-sm">
          <span className="block text-xs text-muted-foreground">Nota</span>
          <input
            className={`${campoCls} w-full`}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!idValido}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          onClick={() =>
            onGuardar({
              telegram_id: Number(telegramId.trim()),
              nombre: nombre.trim(),
              activo: true,
              descripcion: descripcion.trim(),
            })
          }
        >
          Autorizar
        </button>
        <button type="button" className={BOTON_SEC} onClick={onCancelar}>
          Cancelar
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Va el id <strong>numérico</strong>, no el <code className="font-mono">@usuario</code>:
        el arroba se puede cambiar, el id no. Se obtiene escribiéndole a{" "}
        <code className="font-mono">@RawDataBot</code>.
      </p>
    </div>
  );
}
